import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button, Typography, Alert, CircularProgress } from '@mui/material';
import AuthAlert from '../components/AuthAlert';
import AuthCard from '../components/AuthCard';
import PasswordField from '../components/PasswordField';
import PasswordHints from '../components/PasswordHints';
import api from '../api/client';
import { apiError } from '../i18n/errors';
import { colors } from '../colors';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError('Le mot de passe doit contenir au moins un caractère spécial.');
      return;
    }
    if (!/\d/.test(password)) {
      setError('Le mot de passe doit contenir au moins un chiffre.');
      return;
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password/', { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(apiError(err, 'Une erreur est survenue'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthCard title="Lien invalide">
        <AuthAlert>Le lien de réinitialisation est invalide ou a expiré.</AuthAlert>
        <Typography textAlign="center" variant="body2">
          <Link to="/forgot-password" style={{ color: colors.primaryLight, textDecoration: 'none' }}>
            Demander un nouveau lien
          </Link>
        </Typography>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Nouveau mot de passe">
      <Typography variant="h6" sx={{ color: colors.textPrimary, textAlign: 'center' }}>
        Réinitialiser le mot de passe
      </Typography>

      {success ? (
        <>
          <Alert severity="success" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: colors.success, border: '1px solid rgba(34,197,94,0.3)' }}>
            Mot de passe réinitialisé avec succès !
          </Alert>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}
            sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}>
            Se connecter
          </Button>
        </>
      ) : (
        <>
          {error && <AuthAlert>{error}</AuthAlert>}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PasswordField label="Nouveau mot de passe (8 caractères min)" required fullWidth autoFocus
              disabled={loading}
              value={password} onChange={(e) => setPassword(e.target.value)} />
            <PasswordHints value={password} />
            <PasswordField label="Confirmer le mot de passe" required fullWidth
              disabled={loading}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              error={confirm.length > 0 && password !== confirm}
              helperText={confirm.length > 0 && password !== confirm ? 'Les mots de passe ne correspondent pas' : ''}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e); }} />
            <Button type="submit" variant="contained" color="primary" fullWidth size="large"
              disabled={loading}
              sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Réinitialiser'}
            </Button>
          </form>
        </>
      )}

      <Typography textAlign="center" variant="body2" sx={{ color: colors.textSecondary }}>
        <Link to="/login" style={{ color: colors.primaryLight, fontWeight: 600, textDecoration: 'none' }}>
          ← Retour à la connexion
        </Link>
      </Typography>
    </AuthCard>
  );
}
